import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";
import { FormField, useFieldIds } from "../FormField/FormField";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  /** Acción a la derecha del label — un contador de caracteres, por ejemplo. */
  labelAction?: ReactNode;
  hint?: ReactNode;
  /** Su presencia pone el campo en rojo y enlaza `aria-invalid`/`aria-errormessage`. */
  error?: string;
  /** Clases del contenedor del campo (no del `<textarea>`). */
  fieldClassName?: string;
}

/**
 * El área de texto del sistema. Hermano de `Input` y con el mismo trabajo:
 * enlazar label, pista, error y estados de ARIA de una sola forma.
 *
 * ── Por qué existe, si ya estaba escrito ─────────────────────────────
 * El formulario de contacto tenía su propio `MessageField` con el
 * `<textarea>` suelto dentro y las clases de `Input` copiadas a mano.
 * Funcionaba, pero era la única caja de texto del sistema que no podía
 * conectarse a un `<Form>`: sin `error`, sin `aria-invalid`, sin quedarse
 * deshabilitada mientras se envía. Ahora que el mensaje de contacto se
 * valida con el contrato compartido como todo lo demás, el control tiene
 * que vivir donde viven los demás.
 *
 * ── `resize-y`, ni `none` ni `both` ──────────────────────────────────
 * `none` le quita a quien escribe la única forma de ver su propio mensaje
 * completo; `both` deja estirarlo a lo ancho y romper la columna del
 * formulario. Vertical es lo que sirve y lo que no rompe nada.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    labelAction,
    hint,
    error,
    required,
    fieldClassName,
    className,
    id,
    disabled,
    rows = 4,
    "aria-describedby": extraDescribedBy,
    ...rest
  },
  ref,
) {
  const { controlId, hintId, errorId, describedBy, invalid } = useFieldIds(id, {
    hasHint: Boolean(hint),
    hasError: Boolean(error),
  });

  // Como en `Input`: lo interno va PRIMERO y no lo pisa el spread, para que
  // un `aria-describedby` de fuera no borre el enlace con el error.
  const describedByAll =
    [describedBy, extraDescribedBy].filter(Boolean).join(" ") || undefined;

  return (
    <FormField
      label={label}
      labelAction={labelAction}
      hint={hint}
      error={error}
      required={required}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
      className={fieldClassName}
    >
      <textarea
        ref={ref}
        id={controlId}
        rows={rows}
        required={required}
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={describedByAll}
        className={cn(
          // Las mismas clases que `Input`, para que las dos cajas de texto
          // del sistema se vean iguales de verdad y no "casi".
          "block w-full resize-y rounded-2xl border bg-ds-surface px-4 py-3 text-sm text-ds-ink",
          "outline-none transition placeholder:text-ds-ink-4",
          "focus:border-ds-lilac focus:ring-2 focus:ring-ds-lavender-bg-2/50",
          "disabled:cursor-not-allowed disabled:bg-ds-cream-2 disabled:opacity-60",
          error
            ? "border-ds-terracotta focus:border-ds-terracotta focus:ring-ds-terracotta/20"
            : "border-ds-border",
          className,
        )}
        {...rest}
      />
    </FormField>
  );
});
